import {
  ArrowLeftRight,
  Briefcase,
  ChevronDownIcon,
  ChevronRightIcon,

  FileTextIcon,
  FolderIcon,
  Landmark as LandmarkIcon,
  LayoutDashboard,
  LogOut as LogOutIcon,
  PanelLeftIcon,
  PanelRightIcon,
  ShieldCheck as ShieldCheckIcon,
  User as UserIcon,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type NavigationSubItem = {
  id: string;
  label: string;
  path: string;
  isActive?: boolean;
  adminOnly?: boolean;
  superAdminOnly?: boolean;
};

export type NavigationItem = {
  id: string;
  label: string;
  icon: LucideIcon;
  path?: string;
  hasSubmenu: boolean;
  isExpanded?: boolean;
  adminOnly?: boolean;
  submenuItems?: NavigationSubItem[];
};

export const navigationItems: NavigationItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    path: "/dashboard",
    hasSubmenu: false,
  },
  {
    id: "invoice-processing",
    label: "Invoice Processing",
    icon: FileTextIcon,
    path: "/invoices/processing",
    hasSubmenu: false,
  },
  {
    id: "bank-reconciliation",
    label: "Bank Reconciliation",
    icon: ArrowLeftRight,
    path: "/reconciliation",
    hasSubmenu: false,
  },
  {
    id: "files",
    label: "File manager",
    icon: FolderIcon,
    path: "/files",
    hasSubmenu: false,
  },
  {
    id: "tax-requests",
    label: "NRA Tax Requests",
    icon: LandmarkIcon,
    path: "/tax-requests",
    hasSubmenu: false,
  },
  {
    id: "clients",
    label: "Clients",
    icon: Briefcase,
    path: "/clients",
    hasSubmenu: false,
  },
];

export const footerMenuItems: NavigationItem[] = [];

export const navigationIcons = {
  ChevronDownIcon,
  ChevronRightIcon,
  LogOutIcon,
  PanelLeftIcon,
  PanelRightIcon,
  ShieldCheckIcon,
  UserIcon,
};

export type BreadcrumbItem = {
  label: string;
  path?: string;
};

const normalizePath = (pathname: string): string => {
  const [pathWithoutQuery] = pathname.split("?");
  if (pathWithoutQuery === "/") return "/";
  const withoutTrailing = pathWithoutQuery.replace(/\/+$/, "");
  return withoutTrailing || "/";
};

const specialBreadcrumbs: Array<{
  match: (path: string) => boolean;
  breadcrumbs: BreadcrumbItem[];
}> = [
    {
      match: (path) => path === "/dashboard",
      breadcrumbs: [
        { label: "Dashboard" },
      ],
    },
    {
      match: (path) => path === "/process",
      breadcrumbs: [
        { label: "Invoice Processing", path: "/invoices/processing" },
        { label: "Document Processing" },
      ],
    },
    {
      match: (path) => path.startsWith("/review"),
      breadcrumbs: [
        { label: "Invoice Processing", path: "/invoices/processing" },
        { label: "Review" },
      ],
    },
    {
      match: (path) => path === "/reconciliation",
      breadcrumbs: [
        { label: "Bank Reconciliation" },
      ],
    },
  ];

export const getBreadcrumbsForPath = (pathname: string): BreadcrumbItem[] => {
  const normalizedPath = normalizePath(pathname);

  if (normalizedPath === "/") {
    return [];
  }

  for (const { match, breadcrumbs } of specialBreadcrumbs) {
    if (match(normalizedPath)) {
      return breadcrumbs;
    }
  }

  for (const item of [...navigationItems, ...footerMenuItems]) {
    if (item.submenuItems) {
      for (const subItem of item.submenuItems) {
        const subItemPath = normalizePath(subItem.path);
        if (
          subItemPath === normalizedPath ||
          normalizedPath.startsWith(`${subItemPath}/`)
        ) {
          const breadcrumbs: BreadcrumbItem[] = [];
          breadcrumbs.push({ label: item.label, path: item.path });

          breadcrumbs.push({
            label: subItem.label,
            path: subItem.path.includes("?") ? subItem.path.split("?")[0] : subItem.path,
          });

          return breadcrumbs;
        }
      }
    }

    if (item.path && normalizePath(item.path) === normalizedPath) {
      return [
        { label: item.label, path: item.path },
      ];
    }
  }

  const segments = normalizedPath.split("/").filter(Boolean);
  if (segments.length) {
    return segments.map((segment, index) => {
      const path = `/${segments.slice(0, index + 1).join("/")}`;
      return {
        label: segment.charAt(0).toUpperCase() + segment.slice(1),
        path: index === segments.length - 1 ? undefined : path,
      };
    });
  }

  return [];
};
